import { useState, useRef, useEffect, useCallback } from "react";
import { Image as ImageIcon, ImageOff } from "lucide-react";

// Imagen con carga perezosa + reintentos acotados.
// - Placeholder animado mientras carga; no bloquea el render de la página.
// - IntersectionObserver (rootMargin 200px): solo descarga al acercarse al viewport.
// - Ante error (p.ej. una carga lazy cancelada por el navegador): reintenta 3 veces con backoff.
// - Si se agotan los reintentos: estado de error DISTINTO de "sin imagen" (data-state="error").
// - Al volver a entrar en pantalla, reintenta una carga fallida (recupera al hacer scroll).
const MAX_RETRIES = 3;

export const LazyImage = ({
  src,
  alt = "",
  containerClassName = "",
  imgClassName = "object-cover",
  testid,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [inView, setInView] = useState(false);
  const retriesRef = useRef(0);
  const failedRef = useRef(false);
  const timerRef = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    failedRef.current = failed;
  }, [failed]);

  useEffect(() => {
    // Nueva fuente => reinicia todo el estado.
    setLoaded(false);
    setFailed(false);
    setAttempt(0);
    retriesRef.current = 0;
    failedRef.current = false;
  }, [src]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            if (failedRef.current) {
              retriesRef.current = 0;
              failedRef.current = false;
              setFailed(false);
              setAttempt((a) => a + 1);
            }
          } else {
            setInView(false);
          }
        });
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const onError = useCallback(() => {
    if (retriesRef.current < MAX_RETRIES) {
      const n = retriesRef.current + 1;
      retriesRef.current = n;
      timerRef.current = setTimeout(() => setAttempt((a) => a + 1), 400 * n);
    } else {
      failedRef.current = true;
      setFailed(true);
    }
  }, []);

  const showPlaceholder = !src || !loaded || failed;
  const state = failed ? "error" : src ? "loading" : "empty";

  return (
    <div ref={ref} className={`relative overflow-hidden bg-slate-100 ${containerClassName}`} {...rest}>
      {showPlaceholder && (
        <div
          data-testid={testid ? `${testid}-placeholder` : undefined}
          data-state={state}
          className={`absolute inset-0 flex items-center justify-center ${
            failed ? "bg-rose-50 text-rose-300" : src ? "animate-pulse text-slate-300" : "text-slate-300"
          }`}
        >
          {failed ? <ImageOff size={16} /> : <ImageIcon size={16} />}
        </div>
      )}
      {inView && src && !failed && (
        <img
          key={attempt}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          data-testid={testid}
          onLoad={() => setLoaded(true)}
          onError={onError}
          className={`w-full h-full transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"} ${imgClassName}`}
        />
      )}
    </div>
  );
};

export default LazyImage;
